import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Header } from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, ListChecks, MapPin, CheckCircle2, Clock, FileText, GraduationCap, RefreshCw } from "lucide-react";
import { RateUsCard } from "@/components/Feedback";

function money(n) {
  return Number(n) === 0 ? "Free" : `R${Number(n).toFixed(2)}`;
}

const STATUS = {
  submitted: { label: "Submitted", cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  paid: { label: "Paid", cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  pending_payment: { label: "Pending Payment", cls: "bg-amber-100 text-amber-700", icon: Clock },
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-600", icon: Clock },
  under_review: { label: "Under Review", cls: "bg-blue-100 text-blue-700", icon: Clock },
  done: { label: "Done", cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  accepted: { label: "Accepted", cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  rejected: { label: "Not Successful", cls: "bg-rose-100 text-rose-700", icon: Clock },
  cancelled: { label: "Cancelled", cls: "bg-slate-100 text-slate-500", icon: Clock },
};

export default function Applications() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const side = user?.track === "colleges" ? "college" : user?.track === "universities" ? "university" : null;
  const [apps, setApps] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [rechecking, setRechecking] = useState(false);

  const load = async () => {
    const { data } = await api.get("/applications");
    setApps(side ? data.applications.filter((a) => (a.institution_type || "university") === side) : data.applications);
    return data.applications;
  };

  useEffect(() => {
    // Self-heal any payments confirmed at Paystack but missed on redirect/webhook, then load.
    load();
    api.post("/payments/reconcile").then(({ data: r }) => { if (r.reconciled > 0) load(); }).catch(() => {});
  }, []);

  const recheck = async () => {
    setRechecking(true);
    try {
      const { data } = await api.post("/payments/reconcile");
      const updated = await load();
      const stillPending = updated.some((a) => a.status === "pending_payment");
      if (data.reconciled > 0) {
        toast.success(`${data.reconciled} payment${data.reconciled > 1 ? "s" : ""} confirmed and submitted.`);
      } else if (stillPending) {
        toast.info("No confirmed payment found yet. If you've just paid, please wait a minute and try again.");
      } else {
        toast.success("All applications are up to date.");
      }
    } catch {
      toast.error("Could not re-check payments. Please try again.");
    } finally {
      setRechecking(false);
    }
  };

  const downloadPdf = async (id) => {
    setDownloading(id);
    try {
      const res = await api.get(`/applications/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `application-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not download PDF.");
    } finally {
      setDownloading(null);
    }
  };

  if (!apps)
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Header />
        <div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-[#0B2447]" /></div>
      </div>
    );

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Header />
      <div className="max-w-4xl mx-auto px-6 py-10">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">My Profile</p>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-[#0B2447] mt-1">Universities You've Applied To</h1>
        <p className="text-slate-600 mt-2">Every university application you've made, with status and documents.</p>
        <RateUsCard />

        {apps.some((a) => a.status === "pending_payment") && (
          <div className="mt-5 bg-amber-50 border border-amber-200 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3"
            data-testid="pending-payment-banner">
            <div className="flex items-center gap-2 text-sm text-amber-800">
              <Clock className="w-4 h-4 shrink-0" />
              Already paid but still showing "Pending Payment"? Re-check to confirm your payment.
            </div>
            <Button size="sm" variant="outline" onClick={recheck} disabled={rechecking}
              data-testid="recheck-payment-btn"
              className="border-amber-400 text-amber-800 hover:bg-amber-100">
              {rechecking ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-1.5" /> Re-check payment</>}
            </Button>
          </div>
        )}

        {apps.length === 0 ? (
          <div className="text-center py-20">
            <ListChecks className="w-14 h-14 text-slate-300 mx-auto" />
            <p className="text-slate-600 mt-4">{side === "college" ? "You haven't applied to any colleges yet." : "You haven't applied to any universities yet."}</p>
            <Button className="mt-5 bg-[#0B2447] hover:bg-[#153461]" onClick={() => navigate(side === "college" ? "/dashboard/colleges" : "/dashboard/courses")}
              data-testid="view-courses-btn">{side === "college" ? "Browse College Programmes" : "View Qualifying Courses"}</Button>
          </div>
        ) : (
          <div className="mt-8 space-y-5" data-testid="applications-list">
            {apps.map((a, i) => {
              const st = STATUS[a.status] || STATUS.draft;
              const Icon = st.icon;
              return (
                <motion.div key={a.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden"
                  data-testid={`application-${a.id}`}>
                  <div className="flex items-center gap-4 p-5 border-b border-slate-100">
                    <img src={a.university_logo} alt={a.university_name}
                      className="w-14 h-14 rounded-lg object-contain bg-white border border-slate-100 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display font-bold text-lg text-[#0B2447]">{a.university_name}</h3>
                      <p className="text-xs text-slate-400">
                        {a.applicant?.first_name} {a.applicant?.last_name} · Fee {money(a.fee)}
                      </p>
                    </div>
                    <Badge className={`${st.cls} border-0 gap-1.5 py-1.5 px-3`}>
                      <Icon className="w-3.5 h-3.5" /> {st.label}
                    </Badge>
                  </div>
                  <div className="p-5">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Courses applied for</p>
                    <div className="space-y-1.5">
                      {(a.courses || []).map((c) => (
                        <div key={c.course_id} className="flex items-center gap-2 text-sm text-slate-700">
                          <GraduationCap className="w-4 h-4 text-[#F5C32C] shrink-0" />
                          {c.course_name}
                          <span className="text-slate-400 flex items-center gap-1 text-xs">
                            <MapPin className="w-3 h-3" /> {c.campus}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4">
                      <Button variant="outline" size="sm" data-testid={`download-pdf-${a.id}`}
                        disabled={downloading === a.id}
                        onClick={() => downloadPdf(a.id)}
                        className="border-[#0B2447]/30 text-[#0B2447]">
                        {downloading === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileText className="w-4 h-4 mr-1.5" /> Download PDF</>}
                      </Button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
